import Vue from "vue";
import VueRouter, { Route } from "vue-router";
import store from "../store/index";
import {
	SystemPermissions,
	hasAnyPermission,
	hasPermission,
	WellKnownRoles,
	hasRole,
	hasAnyRole,
	User,
} from "@/models/User";

Vue.use(VueRouter);

const routes = [
	{
		path: "/",
		redirect: "/home",
	},
	{
		path: "/login",
		name: "Login",
		component: () => import("@/views/Authentication/Login.vue"),
		meta: {
			guest: true,
		},
	},
	{
		path: "/home",
		redirect: "/sales-orders/quotes",
		// TODO: Restore when home page requirements are developed
		// name: "Home",
		// component: () => import("@/views/Home.vue"),
		// meta: {
		// 	requiresAuth: true,
		// },
	},
	{
		path: "/sales-orders/quotes",
		component: () => import("@/views/SalesOrders/Quotes.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.SalesOrders_Quote_View],
		},
		children: [
			{
				path: "",
				name: "Quotes",
				component: () => import("@/components/SalesOrders/QuotesList.vue"),
			},
			{
				path: "new",
				name: "Quotes",
				component: () => import("@/components/SalesOrders/QuoteView.vue"),
				props: (route: Route) => ({
					initWithCustomer: route.query.initWithCustomer,
				}),
			},
			{
				path: ":quoteId",
				name: "Quote",
				component: () => import("@/components/SalesOrders/QuoteView.vue"),
				props: (route: Route) => ({
					quote: route.params.quoteId ? { quoteId: parseInt(route.params.quoteId) } : null,
					initWithCustomer: route.query.initWithCustomer,
				}),
			},
			{
				path: ":quoteId/payments",
				name: "Quote Payments",
				component: () => import("@/components/SalesOrders/QuoteView.vue"),
				props: (route: Route) => ({
					quote: route.params.quoteId ? { quoteId: parseInt(route.params.quoteId) } : null,
					initWithCustomer: route.query.initWithCustomer,
				}),
			},
			{
				path: ":quoteId/print",
				name: "Quote Print",
				component: () => import("@/components/SalesOrders/QuoteView.vue"),
				props: (route: Route) => ({
					quote: route.params.quoteId ? { quoteId: parseInt(route.params.quoteId) } : null,
					initWithCustomer: route.query.initWithCustomer,
				}),
			},
		],
	},
	{
		path: "/sales-orders/pending-approvals",
		component: () => import("@/views/SalesOrders/PendingApprovals.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.SalesOrders_PendingApproval_View],
		},
		children: [
			{
				path: "",
				name: "Pending Approvals",
				component: () => import("@/components/SalesOrders/PendingApprovalsList.vue"),
			},
			{
				path: ":quoteId",
				name: "Pending Approval",
				component: () => import("@/components/SalesOrders/QuoteView.vue"),
				props: (route: Route) => ({
					quote: route.params.quoteId ? { quoteId: parseInt(route.params.quoteId) } : null,
					pendingApprovalMode: true,
				}),
			},
		],
	},
	{
		path: "/sales-orders/orders",
		component: () => import("@/views/SalesOrders/Orders.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.SalesOrders_Order_View],
		},
		children: [
			{
				path: "",
				name: "Orders",
				component: () => import("@/components/SalesOrders/OrdersList.vue"),
			},
			{
				path: ":orderId",
				name: "Order",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
				}),
			},
			{
				path: ":orderId/payments",
				name: "Order Payments",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
				}),
			},
			{
				path: ":orderId/print",
				name: "Order Print",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
				}),
			},
			{
				path: ":orderId/pick-ticket-print",
				name: "Order Pick Ticket Print",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					pickTicketIds:
						typeof route.query.pickTicketIds === "string"
							? route.query.pickTicketIds.split(",").map(Number)
							: [],
				}),
			},
			{
				path: ":orderId/po-print",
				name: "Order PO Print",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					purchaseOrderIds:
						typeof route.query.purchaseOrderIds === "string"
							? route.query.purchaseOrderIds.split(",").map(Number)
							: [],
				}),
			},
			{
				path: ":orderId/packing-slip-print",
				name: "Order Packing Slip Print",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					purchaseOrderIds:
						typeof route.query.purchaseOrderIds === "string"
							? route.query.purchaseOrderIds.split(",").map(Number)
							: [],
				}),
			},
			{
				path: ":orderId/status",
				name: "Order Status",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
				}),
			},
		],
	},
	{
		path: "/sales-orders/sample-orders",
		component: () => import("@/views/SalesOrders/SampleOrders.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.SalesOrders_SampleOrder_View],
		},
		children: [
			{
				path: "",
				name: "Sample Orders",
				component: () => import("@/components/SalesOrders/SampleOrdersList.vue"),
			},
			{
				path: ":orderId",
				name: "Sample Order",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					isSampleOrder: true,
				}),
			},
			{
				path: ":orderId/payments",
				name: "Sample Order Payments",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					isSampleOrder: true,
				}),
			},
			{
				path: ":orderId/print",
				name: "Sample Order Print",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					isSampleOrder: true,
				}),
			},
			{
				path: ":orderId/pick-ticket-print",
				name: "Sample Order Pick Ticket Print",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					isSampleOrder: true,
					pickTicketIds:
						typeof route.query.pickTicketIds === "string"
							? route.query.pickTicketIds.split(",").map(Number)
							: [],
				}),
				meta: {
					permissions: [SystemPermissions.SalesOrders_OrderStatus_PrintPickTicket],
				},
			},
			{
				path: ":orderId/po-print",
				name: "Sample Order PO Print",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					isSampleOrder: true,
					purchaseOrderIds:
						typeof route.query.purchaseOrderIds === "string"
							? route.query.purchaseOrderIds.split(",").map(Number)
							: [],
				}),
			},
			{
				path: ":orderId/packing-slip-print",
				name: "Sample Order Packing Slip Print",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					isSampleOrder: true,
					purchaseOrderIds:
						typeof route.query.purchaseOrderIds === "string"
							? route.query.purchaseOrderIds.split(",").map(Number)
							: [],
				}),
				meta: {
					permissions: [SystemPermissions.SalesOrders_OrderStatus_PrintPackingSlips],
				},
			},
			{
				path: ":orderId/status",
				name: "Sample Order Status",
				component: () => import("@/components/SalesOrders/OrderView.vue"),
				props: (route: Route) => ({
					order: route.params.orderId ? { salesOrderId: parseInt(route.params.orderId) } : null,
					isSampleOrder: true,
				}),
			},
		],
	},
	{
		path: "/sales-orders/find-related",
		name: "Find Related Orders",
		component: () => import("@/views/SalesOrders/FindRelatedOrders.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.SalesOrders_FindRelatedOrders_Search],
		},
	},
	{
		path: "/purchasing/purchase-orders",
		component: () => import("@/views/Purchasing/PurchaseOrder.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Purchasing_PurchaseOrders_View],
		},
		children: [
			{
				path: "",
				name: "Purchase Orders",
				component: () => import("@/components/Purchasing/PurchaseOrderList.vue"),
			},
			{
				path: ":purchaseOrderId",
				name: "Purchase Order",
				component: () => import("@/components/Purchasing/PurchaseOrderView.vue"),
				props: (route: Route) => ({
					purchaseOrderId: parseInt(route.params.purchaseOrderId),
				}),
			},
			{
				path: ":purchaseOrderId/print",
				name: "Purchase Order Print",
				component: () => import("@/components/Purchasing/PurchaseOrderView.vue"),
				props: (route: Route) => ({
					purchaseOrderId: parseInt(route.params.purchaseOrderId),
				}),
			},
		],
	},
	{
		path: "/purchasing/po-generate",
		name: "PO's To Generate",
		component: () => import("@/views/Purchasing/POToGenerate.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Purchasing_POsToGenerate_Search],
		},
	},
	{
		path: "/purchasing/supplier",
		name: "Supplier",
		component: () => import("@/views/Purchasing/Supplier.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Purchasing_Supplier_Search_View],
		},
	},
	{
		path: "/receiving/receiving-entry",
		component: () => import("@/views/Receiving/ReceivingEntry.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Receiving_ReceivingEntry_View],
		},
		children: [
			{
				path: "",
				name: "Receiving",
				component: () => import("@/components/Receiving/ReceivingList.vue"),
			},
			{
				path: "quick-receive",
				name: "Quick Receive",
				component: () => import("@/components/Receiving/ReceivingView.vue"),
			},
			{
				path: ":purchaseOrderId",
				name: "Receiving Entry",
				component: () => import("@/components/Receiving/ReceivingView.vue"),
				props: (route: Route) => ({
					purchaseOrderId: parseInt(route.params.purchaseOrderId),
				}),
			},
		],
	},
	{
		path: "/receiving/commits",
		name: "Commits",
		component: () => import("@/views/Receiving/Commit.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Receiving_Commits_Search_View],
		},
	},
	{
		path: "/inventory/available-inventory",
		component: () => import("@/views/Inventory/AvailableInventory.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Inventory_AvailableInventory_Search_View],
		},
		children: [
			{
				path: "",
				name: "Available Inventory",
				redirect: "stocked",
			},
			{
				path: "stocked",
				name: "Stocked Items",
				component: () => import("@/views/Inventory/AvailableInventory.vue"),
				props: { defaultTab: 1 },
			},
			{
				path: "all",
				name: "All Items",
				component: () => import("@/views/Inventory/AvailableInventory.vue"),
				props: { defaultTab: 2 },
			},
		],
	},
	{
		path: "/inventory/items",
		component: () => import("@/views/Inventory/InventoryItems.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Inventory_InventoryItems_View_Search],
		},
		children: [
			{
				path: "",
				name: "Inventory Items",
				component: () => import("@/components/Inventory/InventoryItemsList.vue"),
			},
			{
				path: "new",
				name: "New Inventory Item",
				component: () => import("@/components/Inventory/InventoryItemView.vue"),
				props: {
					item: null,
					activeStep: 1,
				},
			},
			{
				path: ":inventoryItemId",
				name: "Inventory Item",
				component: () => import("@/components/Inventory/InventoryItemView.vue"),
				props: (route: Route) => ({
					inventoryItemId: parseInt(route.params.inventoryItemId),
					activeStep: 1,
				}),
			},
			{
				path: ":inventoryItemId/package",
				name: "Inventory Item Package",
				component: () => import("@/components/Inventory/InventoryItemView.vue"),
				props: (route: Route) => ({
					inventoryItemId: parseInt(route.params.inventoryItemId),
					activeStep: 2,
				}),
			},
			{
				path: ":inventoryItemId/subitems",
				name: "Inventory Item Sub-Items",
				component: () => import("@/components/Inventory/InventoryItemView.vue"),
				props: (route: Route) => ({
					inventoryItemId: parseInt(route.params.inventoryItemId),
					activeStep: 3,
				}),
			},
			{
				path: ":inventoryItemId/inventory",
				name: "Inventory Item Information",
				component: () => import("@/components/Inventory/InventoryItemView.vue"),
				props: (route: Route) => ({
					inventoryItemId: parseInt(route.params.inventoryItemId),
					activeStep: 4,
				}),
			},
			{
				path: ":inventoryItemId/sizing",
				name: "Inventory Item Sizing",
				component: () => import("@/components/Inventory/InventoryItemView.vue"),
				props: (route: Route) => ({
					inventoryItemId: parseInt(route.params.inventoryItemId),
					activeStep: 5,
				}),
			},
			{
				path: ":inventoryItemId/colors",
				name: "Inventory Item Colors",
				component: () => import("@/components/Inventory/InventoryItemView.vue"),
				props: (route: Route) => ({
					inventoryItemId: parseInt(route.params.inventoryItemId),
					activeStep: 6,
				}),
			},
			{
				path: ":inventoryItemId/vendor",
				name: "Inventory Item Vendor",
				component: () => import("@/components/Inventory/InventoryItemView.vue"),
				props: (route: Route) => ({
					inventoryItemId: parseInt(route.params.inventoryItemId),
					activeStep: 7,
				}),
			},
		],
	},
	{
		path: "/inventory/bin-utility",
		component: () => import("@/views/Inventory/BinUtility.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Inventory_BinUtility_View_Search],
		},
		children: [
			{
				path: "",
				name: "Bin Utility",
				component: () => import("@/components/Inventory/BinUtilityList.vue"),
			},
			{
				path: ":inventoryItemId",
				name: "Bin Utility Item",
				component: () => import("@/components/Inventory/BinUtilityInventory.vue"),
				props: (route: Route) => ({
					inventoryItemId: parseInt(route.params.inventoryItemId),
				}),
			},
		],
	},
	{
		path: "/inventory/size-charts",
		component: () => import("@/views/Inventory/SizeCharts.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Inventory_SizeChart_View_Search],
		},
		children: [
			{
				path: "",
				name: "Size Charts",
				component: () => import("@/components/Inventory/SizeChartsList.vue"),
			},
			{
				path: "new",
				name: "New Size Chart",
				component: () => import("@/components/Inventory/SizeChartView.vue"),
			},
			{
				path: ":sizeChartId",
				name: "Size Chart",
				component: () => import("@/components/Inventory/SizeChartView.vue"),
				props: (route: Route) => ({
					sizeChartId: parseInt(route.params.sizeChartId),
				}),
			},
		],
	},
	{
		path: "/inventory/color-palette",
		component: () => import("@/views/Inventory/ColorPalette.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Inventory_ColorPalette_View_Search],
		},
		children: [
			{
				path: "",
				name: "Color Palette",
				component: () => import("@/components/Inventory/ColorPaletteList.vue"),
			},
			{
				path: "new",
				name: "New Color Palette",
				component: () => import("@/components/Inventory/PaletteView.vue"),
				props: {
					palette: null,
				},
			},
			{
				path: ":colorPaletteId",
				name: "Color Palette Item",
				component: () => import("@/components/Inventory/PaletteView.vue"),
				props: (route: Route) => ({
					colorPaletteId: parseInt(route.params.colorPaletteId),
				}),
			},
		],
	},
	{
		path: "/inventory/package-category",
		component: () => import("@/views/Inventory/PackageCategory.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Inventory_PackageCategory_View_Search],
		},
		children: [
			{
				path: "",
				name: "Package Categories",
				component: () => import("@/components/Inventory/PackageCategoryList.vue"),
			},
			{
				path: "new",
				name: "New Package Category",
				component: () => import("@/components/Inventory/PackageCategoryView.vue"),
			},
			{
				path: ":packageCategoryId",
				name: "Package Category",
				component: () => import("@/components/Inventory/PackageCategoryView.vue"),
				props: (route: Route) => ({
					packageCategoryId: parseInt(route.params.packageCategoryId),
				}),
			},
		],
	},
	{
		path: "/inventory/sub-item-category",
		component: () => import("@/views/Inventory/SubItemCategory.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Inventory_SubItemCategory_View_Search],
		},
		children: [
			{
				path: "",
				name: "Sub-Item Categories",
				component: () => import("@/components/Inventory/SubItemCategoryList.vue"),
			},
			{
				path: "new",
				name: "New Sub-Item Category",
				component: () => import("@/components/Inventory/SubItemCategoryView.vue"),
			},
			{
				path: ":subItemCategoryId",
				name: "Sub-Item Category",
				component: () => import("@/components/Inventory/SubItemCategoryView.vue"),
				props: (route: Route) => ({
					subItemCategoryId: parseInt(route.params.subItemCategoryId),
				}),
			},
		],
	},
	{
		path: "/inventory/position-of-letters",
		name: "Position Of Letters",
		component: () => import("@/views/Inventory/PositionOfLetters.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Inventory_PositionOfLetters_View],
		},
	},
	{
		path: "/inventory/processes",
		name: "Process Types",
		component: () => import("@/views/Inventory/ProcessTypes.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Inventory_ProcessTypes_View],
		},
	},
	// {
	// 	path: "/inventory/demand-history",
	// 	name: "Demand History",
	// 	component: () => import("@/views/Inventory/DemandHistory.vue"),
	// 	meta: {
	// 		requiresAuth: true,
	// 		permissions: [SystemPermissions.Reports_DemandHistory_ALL],
	// 	},
	// },
	{
		path: "/relationships/customer",
		component: () => import("@/views/Relationships/Customer.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Relationships_Customer_ALL],
		},
		children: [
			{
				path: "",
				name: "Customers",
				component: () => import("@/components/Relationships/CustomerList.vue"),
				props: (route: Route) => ({
					addressTab: route.query.addressTab === "true",
					returnToQuote: route.query.initToNewCustomerView === "true",
					quoteId: route.query.quoteId,
					orderId: route.query.orderId,
				}),
			},
			{
				path: "contacts",
				name: "Customer Contacts",
				component: () => import("@/components/Relationships/CustomerList.vue"),
				props: (route: Route) => ({
					activeTab: 2,
					addressTab: route.query.addressTab === "true",
					returnToQuote: route.query.initToNewCustomerView === "true",
					quoteId: route.query.quoteId,
					orderId: route.query.orderId,
				}),
			},
			{
				path: "new",
				name: "New Customer",
				component: () => import("@/components/Relationships/CustomerView.vue"),
				props: (route: Route) => ({
					customerId: null,
					addressTab: route.query.addressTab === "true",
					returnToQuote: route.query.initToNewCustomerView === "true",
					quoteId: route.query.quoteId,
					orderId: route.query.orderId,
				}),
			},
			{
				path: ":customerId",
				name: "Customer",
				component: () => import("@/components/Relationships/CustomerView.vue"),
				props: (route: Route) => ({
					customerId: parseInt(route.params.customerId),
					addressTab: route.query.addressTab === "true",
					returnToQuote: route.query.initToNewCustomerView === "true",
					quoteId: route.query.quoteId,
					orderId: route.query.orderId,
				}),
			},
		],
	},
	{
		path: "/relationships/vendor",
		component: () => import("@/views/Relationships/Vendor.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Relationships_Vendor_ALL],
		},
		children: [
			{
				path: "",
				name: "Vendors",
				component: () => import("@/components/Relationships/VendorList.vue"),
			},
			{
				path: "contacts",
				name: "Vendor Contacts",
				component: () => import("@/components/Relationships/VendorList.vue"),
				props: { activeTab: 2 },
			},
			{
				path: "new",
				name: "New Vendor",
				component: () => import("@/components/Relationships/VendorView.vue"),
			},
			{
				path: ":vendorId",
				name: "Vendor",
				component: () => import("@/components/Relationships/VendorView.vue"),
				props: (route: Route) => ({
					vendorId: parseInt(route.params.vendorId),
				}),
			},
		],
	},
	{
		path: "/relationships/rep",
		name: "Rep",
		component: () => import("@/views/Relationships/Rep.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Relationships_Rep_ALL],
		},
	},
	{
		path: "/settings/customer-category",
		name: "Customer Category",
		component: () => import("@/views/Settings/CustomerCategory.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_CustomerCategory_ALL],
		},
	},
	{
		path: "/settings/system-category",
		name: "System Category",
		component: () => import("@/views/Settings/SystemCategory.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_SystemCategory_ALL],
		},
	},
	{
		path: "/settings/payment-method",
		name: "Payment Method",
		component: () => import("@/views/Settings/PaymentMethod.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_PaymentMethod_ALL],
		},
	},
	{
		path: "/settings/payment-terms",
		name: "Payment Terms",
		component: () => import("@/views/Settings/PaymentTerms.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_PaymentTerms_ALL],
		},
	},
	{
		path: "/settings/shipping-method",
		name: "Shipping Method",
		component: () => import("@/views/Settings/ShippingMethod.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_ShippingMethod_ALL],
		},
	},
	{
		path: "/settings/messages",
		name: "Messages",
		component: () => import("@/views/Settings/Messages.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_Messages_ALL],
		},
	},
	{
		path: "/settings/security",
		component: () => import("@/views/Settings/Security.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_Security_ALL],
		},
		children: [
			{
				path: "",
				redirect: "users",
			},
			{
				path: "users",
				name: "Users",
				component: () => import("@/views/Settings/Security.vue"),
			},
			{
				path: "roles",
				name: "Security Roles",
				component: () => import("@/views/Settings/Security.vue"),
			},
		],
	},
	{
		path: "/settings/printer-status",
		name: "Printer Status",
		component: () => import("@/views/Settings/PrinterStatus.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_PrintingLog_ALL],
		},
	},
	{
		path: "/settings/printers",
		name: "Printers",
		component: () => import("@/views/Settings/Printers.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_Printers_ALL],
		},
	},
	{
		path: "/settings/zip-tax",
		name: "Zip Tax Rate",
		component: () => import("@/views/Settings/ZipTaxRate.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_ZipTaxRate_ALL],
		},
	},
	{
		path: "/settings/package-types",
		name: "Package Types",
		component: () => import("@/views/Settings/PackageTypes.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Settings_ShippingMethod_ALL],
		},
	},
	{
		path: "/settings/desktop-app",
		name: "Desktop App",
		component: () => import("@/views/Settings/DesktopApp.vue"),
		meta: {
			requiresAuth: true,
		},
	},
	{
		path: "/settings/account",
		name: "Account Settings",
		component: () => import("@/views/Settings/AccountSettings.vue"),
		meta: {
			requiresAuth: true,
		},
	},
	{
		path: "/reports",
		name: "Reports",
		component: () => import("@/views/Reports/PowerBiReports.vue"),
		meta: {
			requiresAuth: true,
		},
	},
	{
		path: "/forbidden",
		name: "Forbidden",
		component: () => import("@/views/Authentication/Forbidden.vue"),
	},
	{
		path: "/shipper",
		name: "Shipper",
		component: () => import("@/views/Shipper/Dashboard.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Shipper_Shipping_ViewPickTicket],
		},
	},
	{
		path: "/shipper/pick-ticket/:pickTicketId/print",
		name: "Shipper Pick Ticket Print",
		component: () => import("@/views/Shipper/PickTicketPrint.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Shipper_Shipping_ViewPickTicket],
		},
	},
	{
		path: "/shipper/recent-activity",
		name: "Shipper Recent Activity",
		component: () => import("@/views/Shipper/RecentActivity.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Shipper_RecentActivity_View],
		},
	},
	{
		path: "/shipper/end-of-day",
		name: "End of Day Report",
		component: () => import("@/views/Shipper/EndOfDay.vue"),
		meta: {
			requiresAuth: true,
			permissions: [SystemPermissions.Shipper_EndOfDay_View],
		},
	},
	// Auto-print routes - accessible via print tokens without session authentication
	{
		path: "/print/purchase-order/:purchaseOrderId",
		name: "Auto Print Purchase Order",
		component: () => import("@/views/Print/AutoPrintPurchaseOrder.vue"),
		props: (route: Route) => ({
			purchaseOrderId: parseInt(route.params.purchaseOrderId),
			printToken: route.query.printToken as string,
			printedBy: route.query.printedBy as string || null,
		}),
		meta: {
			printTokenAuth: true, // Uses print token instead of session auth
		},
	},
	{
		path: "/print/pick-ticket/:pickTicketId",
		name: "Auto Print Pick Ticket",
		component: () => import("@/views/Print/AutoPrintPickTicket.vue"),
		props: (route: Route) => ({
			pickTicketId: parseInt(route.params.pickTicketId),
			printToken: route.query.printToken as string,
			printedBy: route.query.printedBy as string || null,
		}),
		meta: {
			printTokenAuth: true,
		},
	},
	{
		path: "/print/packing-slip/:purchaseOrderId",
		name: "Auto Print Packing Slip",
		component: () => import("@/views/Print/AutoPrintPackingSlip.vue"),
		props: (route: Route) => ({
			purchaseOrderId: parseInt(route.params.purchaseOrderId),
			printToken: route.query.printToken as string,
			printedBy: route.query.printedBy as string || null,
		}),
		meta: {
			printTokenAuth: true,
		},
	},
];

const router = new VueRouter({
	mode: "history",
	base: process.env.BASE_URL,
	routes,
});

//redirects user if trying to access a page that requires login or a page where guest mode is allowed
router.beforeEach(async (to, from, next) => {
	// Allow print token authenticated routes to bypass session authentication
	// These routes use short-lived, single-use tokens validated by the backend
	if (to.meta?.printTokenAuth && to.query.printToken) {
		next();
		return;
	}

	if (!store.getters.isLoggedIn && to.path !== "/login") {
		await store.dispatch("setRedirect", to.fullPath);
	}

	if (to.matched.some((record) => record.meta.requiresAuth)) {
		if (store.getters.isLoggedIn) {
			try {
				await store.dispatch("refreshUserInfo");
				const user: User = store.getters.userInfo;

			// Check if user is a shipper-only user (no admin privileges)
			if (hasRole(user, WellKnownRoles.SHIPPER) && !hasAnyRole(user, [WellKnownRoles.ADMIN, WellKnownRoles.SUPER_ADMIN])) {
				// Shipper-only users can only access shipper routes and account settings
				// Redirect them from home, root, or any non-shipper route to shipper dashboard
				if (!to.path.startsWith('/shipper') && !to.path.startsWith('/settings/account')) {
					next('/shipper');
					return;
				}
			}

			// Check if non-shipper trying to access shipper routes
			if (to.meta?.restrictToRole && to.meta?.roles) {
				if (!hasAnyRole(user, to.meta.roles) && !hasAnyRole(user, [WellKnownRoles.ADMIN, WellKnownRoles.SUPER_ADMIN])) {
					next('/forbidden');
					return;
				}
			}

			// Collect permissions from all matched routes (parent and child)
			const permissions = to.matched
				.flatMap((record) => record.meta?.permissions || [])
				.filter((p): p is string => !!p);

			if (permissions.length === 0) {
				next();
				return;
			}

			// admin have access to all
			if (hasAnyRole(user, [WellKnownRoles.ADMIN, WellKnownRoles.SUPER_ADMIN])) {
				next();
				return;
			}

			// Shipper role has implicit access to all shipper routes (role-based, not permission-based)
			if (hasRole(user, WellKnownRoles.SHIPPER) && to.path.startsWith('/shipper')) {
				next();
				return;
			}

			if (hasAnyPermission(user, permissions)) {
				console.log(
					`User ${user.username} with roles ${user.roles?.map(
						(role) => role.name
					)} and permissions ${user.permissions?.map(
						(permission) => "\n" + permission.name
					)} has access to ${to.path} with required permission ${permissions}`
				);
				next();
				return;
			} else {
				// TODO [belskie|2023-10-12] determine how to inform user they don't have access
				// to the page they are trying to access. We should probably prevent routing to the page, keep
				// the user on the current page, and display a message to the user.
				next("/forbidden");
				return;
			}
			} catch (error) {
				// Session is invalid, clear it and redirect to login
				console.error("Session validation failed in router:", error);
				await store.dispatch("logout");
				next("/login");
				return;
			}
		}

		next("/login");
	} else {
		if (to.matched.some((record) => record.meta.guest)) {
			if (store.getters.isLoggedIn) {
				next("/");
				return;
			}
			next();
		} else {
			next();
		}
	}
});

export default router;
